import type { Point, Rect, Size } from "../../common/geometry.js";

/** Where a dragged window lands when it is let go at an edge of the work area. */
export type SnapZone = "left" | "right" | "top" | "top-left" | "top-right" | "bottom-left" | "bottom-right";

/** `tileWindows`: `vertical` puts windows side by side, `horizontal` stacks them, as MDI does. */
export type TileOrientation = "horizontal" | "vertical";

/**
 * The zone a pointer at `p` is asking for, or null.
 *
 * Windows' own rule: the left and right edges take half the area, the top edge maximizes,
 * and a corner takes a quarter. A corner is the edge band on one side within `corner` of the
 * top or bottom; the bottom edge on its own means nothing.
 */
export function snapZoneAt(p: Point, area: Rect, edge: number, corner = edge * 4): SnapZone | null {
  const left = p.x <= area.x + edge;
  const right = p.x >= area.x + area.width - edge;
  const top = p.y <= area.y + edge;
  const nearTop = p.y <= area.y + corner;
  const nearBottom = p.y >= area.y + area.height - corner;
  if (left || right) {
    const side = left ? "left" : "right";
    if (nearTop) return `top-${side}`;
    if (nearBottom) return `bottom-${side}`;
    return side;
  }
  return top ? "top" : null;
}

/** The rectangle a zone stands for. `top` is the whole area: maximizing is the window's job. */
export function snapRect(zone: SnapZone, area: Rect): Rect {
  const halfW = Math.round(area.width / 2);
  const halfH = Math.round(area.height / 2);
  const rightX = area.x + halfW;
  const bottomY = area.y + halfH;
  switch (zone) {
    case "top":
      return { ...area };
    case "left":
      return { x: area.x, y: area.y, width: halfW, height: area.height };
    case "right":
      return { x: rightX, y: area.y, width: area.width - halfW, height: area.height };
    case "top-left":
      return { x: area.x, y: area.y, width: halfW, height: halfH };
    case "top-right":
      return { x: rightX, y: area.y, width: area.width - halfW, height: halfH };
    case "bottom-left":
      return { x: area.x, y: bottomY, width: halfW, height: area.height - halfH };
    case "bottom-right":
      return { x: rightX, y: bottomY, width: area.width - halfW, height: area.height - halfH };
  }
}

/**
 * Cascade `sizes` from the work area's top-left, each `step` down and right of the last.
 *
 * A window keeps its own size unless that would run past the area, and the cascade starts
 * over at the top-left when the next step would no longer fit, as Win32's does.
 */
export function cascadeRects(sizes: readonly Size[], area: Rect, step: number): Rect[] {
  let offset = 0;
  return sizes.map((size) => {
    let width = Math.min(size.width, area.width);
    let height = Math.min(size.height, area.height);
    if (offset > 0 && (offset + width > area.width || offset + height > area.height)) offset = 0;
    width = Math.min(width, area.width - offset);
    height = Math.min(height, area.height - offset);
    const r = { x: area.x + offset, y: area.y + offset, width, height };
    offset += step;
    return r;
  });
}

/**
 * Tile `count` windows over the work area with no gaps and no overlap.
 *
 * Up to three share one row (`vertical`) or one column (`horizontal`). Past that it is a grid
 * of ceil(sqrt(count)) lines, and the last line takes the windows left over at a larger size,
 * which is what Win32's MDI tiling does with an odd number.
 */
export function tileRects(count: number, area: Rect, orientation: TileOrientation): Rect[] {
  if (count <= 0) return [];
  const lines = count <= 3 ? 1 : Math.ceil(Math.sqrt(count));
  const perLine = Math.floor(count / lines);
  const extra = count % lines;
  const vertical = orientation === "vertical";
  const along = vertical ? area.height : area.width;
  const across = vertical ? area.width : area.height;
  const rects: Rect[] = [];
  for (let line = 0; line < lines; line++) {
    // The last `extra` lines take one more window each.
    const n = perLine + (line >= lines - extra ? 1 : 0);
    const a0 = Math.round((line * along) / lines);
    const a1 = Math.round(((line + 1) * along) / lines);
    for (let i = 0; i < n; i++) {
      const b0 = Math.round((i * across) / n);
      const b1 = Math.round(((i + 1) * across) / n);
      rects.push(
        vertical
          ? { x: area.x + b0, y: area.y + a0, width: b1 - b0, height: a1 - a0 }
          : { x: area.x + a0, y: area.y + b0, width: a1 - a0, height: b1 - b0 },
      );
    }
  }
  return rects;
}
